import React, { useRef, useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import SchoolServiceAdmin from '../../services/SchoolServiceAdmin';
import 'chart.js/auto';

const AdminDisplayTeacherAttendanceCheck = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [isAttendanceChecksVisible, setIsAttendanceChecksVisible] =
    useState(true);
  const [loading, setLoading] = useState(false);

  const startDateRef = useRef();
  const endDateRef = useRef();

  // Automatically fetch past 2 weeks of data on mount
  useEffect(() => {
    const today = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(today.getDate() - 21);

    const formattedToday = today.toISOString().split('T')[0];
    const formattedTwoWeeksAgo = twoWeeksAgo
      .toISOString()
      .split('T')[0];

    setStartDate(formattedTwoWeeksAgo);
    setEndDate(formattedToday);

    fetchAttendance(formattedTwoWeeksAgo, formattedToday);
  }, []);

  const fetchAttendance = async (start, end) => {
    try {
      setLoading(true);
      const response =
        await SchoolServiceAdmin.getTeacherAttendanceCheckOnDateRange(
          start,
          end
        );
      setAttendanceData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    if (name === 'start') setStartDate(value);
    else if (name === 'end') setEndDate(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }
    fetchAttendance(startDate, endDate);
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setAttendanceData([]);
    if (startDateRef.current) startDateRef.current.value = '';
    if (endDateRef.current) endDateRef.current.value = '';
  };

  const generateChartData = (dateChecks) => ({
    labels: dateChecks.map((check) => check.dateEntity.date),
    datasets: [
      {
        data: dateChecks.map(() => 1),
        backgroundColor: dateChecks.map((check) =>
          check.checked ? '#22c55e' : '#ef4444'
        ),
        borderColor: '#ffffff',
        borderWidth: 2,
      },
    ],
  });

  const generateChartOptions = (dateChecks) => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const date = tooltipItem.label;
            const checked = dateChecks[tooltipItem.dataIndex].checked;
            return `${date}: ${checked ? 'Checked' : 'Unchecked'}`;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  });

  return (
    <>
      <h1 className="md:text-2xl text-2xl text-center font-semibold text-cyan-900 py-4 leading-snug">
        View Teacher Attendance Checks by Date Range
        <br />
        <span className="text-xl text-gray-600 font-normal">
          التحقق من أخذ الحضور والغياب من المعلمات
        </span>
      </h1>

      {/* Toggle Button */}
      <div className="inline-flex items-center justify-center w-full">
        <button
          onClick={() =>
            setIsAttendanceChecksVisible(!isAttendanceChecksVisible)
          }
          className="rounded bg-cyan-700 text-white my-3 px-6 py-2 font-semibold hover:bg-cyan-800"
        >
          {isAttendanceChecksVisible ? 'Hide' : 'Show'}
        </button>
      </div>

      {/* Filters */}
      {isAttendanceChecksVisible && (
        <div className="w-full flex justify-center">
          <form onSubmit={handleSubmit} className="text-center">
            <input
              ref={startDateRef}
              type="date"
              name="start"
              value={startDate}
              onChange={handleDateChange}
              className="w-60 block px-3 py-2 border rounded-md focus:outline-none focus:border-blue-500"
            />
            <input
              ref={endDateRef}
              type="date"
              name="end"
              value={endDate}
              onChange={handleDateChange}
              className="w-60 block px-3 py-2 border rounded-md focus:outline-none focus:border-blue-500 mt-2"
            />
            <button
              type="submit"
              className="rounded text-white p-3 m-2 font-semibold bg-blue-600 hover:bg-blue-700"
            >
              Fetch
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="rounded bg-gray-600 p-3 m-2 text-white font-semibold hover:bg-gray-700"
            >
              Clear and Hide
            </button>
          </form>
        </div>
      )}

      {/* Data Display */}
      {isAttendanceChecksVisible && (
        <div className="py-8">
          {loading ? (
            <div className="text-center text-gray-500">
              Loading...
            </div>
          ) : attendanceData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {attendanceData.map((teacher, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg shadow-md bg-white p-4 hover:shadow-lg transition"
                >
                  <h2 className="text-lg font-semibold text-cyan-900 mb-2 text-center">
                    {teacher.teacherFirstName}{' '}
                    {teacher.teacherLastName}{' '}
                    <span className="text-gray-600 text-sm">
                      ({teacher.classAbbrev})
                    </span>
                  </h2>

                  {teacher.dateChecks.length > 0 ? (
                    <div className="flex flex-col items-center">
                      {/* Chart */}
                      <div className="w-full h-40 mb-3">
                        <Pie
                          data={generateChartData(teacher.dateChecks)}
                          options={generateChartOptions(
                            teacher.dateChecks
                          )}
                        />
                      </div>

                      {/* Scrollable date list */}
                      <div className="w-full max-h-40 overflow-y-auto border-t border-gray-100 pt-2">
                        {teacher.dateChecks.map((check, idx) => (
                          <div
                            key={idx}
                            className="py-2 border-b border-gray-100 text-sm"
                          >
                            <p className="font-medium">
                              Date: {check.dateEntity.date}
                            </p>
                            <p>
                              Checked:{' '}
                              <span
                                className={
                                  check.checked
                                    ? 'text-green-700 font-semibold'
                                    : 'text-red-600 font-semibold'
                                }
                              >
                                {check.checked ? 'Yes' : 'No'}
                              </span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 italic py-2">
                      No attendance records for this range
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              No attendance data found for this range.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdminDisplayTeacherAttendanceCheck;
